import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, Pause, Play, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { moduleShowcaseItems } from './landing-data';

const CACHE_BUST = '?v=20260404a';

export const ProductTour = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [direction, setDirection] = useState('right');
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef(null);
  const touchRef = useRef({ startX: 0, startY: 0, isDragging: false });
  const cardRef = useRef(null);

  const totalSteps = moduleShowcaseItems.length;
  const current = moduleShowcaseItems[activeStep];
  const IconComp = current.icon;

  const goToStep = useCallback((idx) => {
    if (isAnimating || idx === activeStep) return;
    setDirection(idx > activeStep ? 'right' : 'left');
    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep(idx);
      setTimeout(() => setIsAnimating(false), 50);
    }, 200);
  }, [activeStep, isAnimating]);

  const next = useCallback(() => {
    const nextIdx = (activeStep + 1) % totalSteps;
    goToStep(nextIdx);
  }, [activeStep, totalSteps, goToStep]);

  const prev = useCallback(() => {
    const prevIdx = (activeStep - 1 + totalSteps) % totalSteps;
    goToStep(prevIdx);
  }, [activeStep, totalSteps, goToStep]);

  // Auto-play
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(next, 4000);
    }
    return () => clearInterval(timerRef.current);
  }, [isPlaying, next]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  // Touch / drag handlers
  const onTouchStart = useCallback((e) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
    touchRef.current.isDragging = true;
    touchRef.current.scrollCancelled = false;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!touchRef.current.isDragging || touchRef.current.scrollCancelled) return;
    const dx = Math.abs(e.touches[0].clientX - touchRef.current.startX);
    const dy = Math.abs(e.touches[0].clientY - touchRef.current.startY);
    // If vertical movement dominates, release the touch for native scrolling
    if (dy > dx && dy > 10) {
      touchRef.current.isDragging = false;
      touchRef.current.scrollCancelled = true;
    }
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (!touchRef.current.isDragging) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    touchRef.current.isDragging = false;
    // Only trigger if horizontal swipe > 50px and more horizontal than vertical
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      setIsPlaying(false);
      if (dx < 0) next();
      else prev();
    }
  }, [next, prev]);

  // Mouse drag handlers (desktop)
  const onMouseDown = useCallback((e) => {
    touchRef.current.startX = e.clientX;
    touchRef.current.isDragging = true;
    setIsPlaying(false);
  }, []);

  const onMouseUp = useCallback((e) => {
    if (!touchRef.current.isDragging) return;
    const dx = e.clientX - touchRef.current.startX;
    touchRef.current.isDragging = false;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
  }, [next, prev]);

  const progress = ((activeStep + 1) / totalSteps) * 100;

  return (
    <section id="product-tour" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-200 rounded-full px-5 py-2 mb-5">
            <Play className="h-3.5 w-3.5 text-cyan-600 fill-cyan-600" />
            <span className="text-cyan-700 text-sm font-bold tracking-wide uppercase">Interactive Tour</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
            Walk Through Every Module
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Swipe or use arrows to browse our {moduleShowcaseItems.length} modules
          </p>
        </div>

        {/* Progress bar */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Step {activeStep + 1} of {totalSteps}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="tour-autoplay-btn"
                title={isPlaying ? 'Pause auto-play' : 'Auto-play tour'}
              >
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <button
                onClick={() => { setActiveStep(0); setIsPlaying(false); }}
                className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="tour-restart-btn"
                title="Restart tour"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Main tour content */}
        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Module sidebar - scrollable list (desktop only) */}
          <div className="hidden lg:block">
            <div className="bg-slate-900 rounded-2xl p-3 max-h-[560px] overflow-y-auto custom-scrollbar">
              {moduleShowcaseItems.map((item, idx) => {
                const SideIcon = item.icon;
                const isActive = idx === activeStep;
                return (
                  <button
                    key={idx}
                    onClick={() => goToStep(idx)}
                    data-testid={`tour-step-${idx}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 mb-1 ${
                      isActive
                        ? 'bg-white/10 border border-white/20 shadow-lg'
                        : 'hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${isActive ? item.bgColor : 'bg-slate-800'}`}>
                      <SideIcon className={`h-4 w-4 ${isActive ? item.iconColor : 'text-slate-500'}`} />
                    </div>
                    <span className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-slate-400'}`}>
                      {item.title}
                    </span>
                    {isActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content area with swipe + arrows */}
          <div className="relative">
            {/* Overlay arrow buttons - always visible */}
            <button
              onClick={prev}
              data-testid="tour-prev-btn"
              className="absolute left-2 top-[30%] -translate-y-1/2 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/90 backdrop-blur shadow-lg border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 active:scale-95 transition-all"
              aria-label="Previous module"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              data-testid="tour-next-btn"
              className="absolute right-2 top-[30%] -translate-y-1/2 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/90 backdrop-blur shadow-lg border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 active:scale-95 transition-all"
              aria-label="Next module"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* Swipeable card */}
            <div
              ref={cardRef}
              className="overflow-hidden md:cursor-grab md:active:cursor-grabbing"
              style={{ touchAction: 'pan-y' }}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              onMouseLeave={() => { touchRef.current.isDragging = false; }}
            >
              <div className={`transition-all duration-300 ease-out ${
                isAnimating
                  ? direction === 'right' ? 'opacity-0 translate-x-8' : 'opacity-0 -translate-x-8'
                  : 'opacity-100 translate-x-0'
              }`}>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Screenshot */}
                  <div className="relative bg-slate-100 aspect-video overflow-hidden">
                    <img
                      src={current.screenshot.includes('?') ? current.screenshot : `${current.screenshot}${CACHE_BUST}`}
                      alt={current.title}
                      className="w-full h-full object-cover object-top pointer-events-none"
                      loading="lazy"
                      draggable="false"
                    />
                    {/* Module badge overlay */}
                    <div className="absolute top-3 left-12 sm:left-4 flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md">
                      <div className={`p-1 rounded-md ${current.bgColor}`}>
                        <IconComp className={`h-4 w-4 ${current.iconColor}`} />
                      </div>
                      <span className="text-sm font-bold text-slate-900">{current.title}</span>
                    </div>
                  </div>

                  {/* Info panel */}
                  <div className="p-4 sm:p-6">
                    <p className="text-slate-600 text-sm sm:text-base mb-4 sm:mb-5">{current.description}</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {current.features.map((f, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs sm:text-sm text-slate-600">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile: compact step indicator + nav */}
            <div className="lg:hidden mt-4 flex items-center justify-between gap-3">
              <Button
                variant="outline" size="sm" onClick={prev}
                className="border-slate-300 text-slate-600 hover:bg-slate-50 px-3"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Prev
              </Button>
              <span className="text-xs sm:text-sm text-slate-500 font-medium text-center flex-1 truncate">
                {current.title}
              </span>
              <Button
                variant="outline" size="sm" onClick={next}
                className="border-slate-300 text-slate-600 hover:bg-slate-50 px-3"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Mobile mini dots - grouped */}
            <div className="lg:hidden flex items-center justify-center gap-[3px] mt-3 flex-wrap">
              {moduleShowcaseItems.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToStep(idx)}
                  className={`transition-all duration-300 rounded-full ${
                    idx === activeStep
                      ? 'w-5 h-1.5 bg-cyan-500'
                      : 'w-1.5 h-1.5 bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
      `}</style>
    </section>
  );
};
